import { Tooltip } from "@mui/material";
import {
  Grid,
  GridColumn,
  GridContextMenuEvent,
  GridExpandChangeEvent,
  GridHeaderCellProps
} from "@progress/kendo-react-grid";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { CustomCell } from "../../../components/commons/grid-cell/CustomCell";
import { formatDate } from "../../../constante/generic";
import { planoHeaders } from "../../../constante/headers/planoHeaders";
import useGetPollingInterval from "../../../hooks/useGetPollingInterval";
import { Header } from "../../../models/Header";
import { Planogramme } from "../../../models/Planogramme";
import {
  planoAPI,
  useGetEnsembleQuery,
  useGetMagasinsQuery,
  useGetPlanoQuery,
} from "../../../redux/features/cache";
import { useAppDispatch } from "../../../redux/store";
import GridWrapper from "../../grid/GridWrapper";
import DetaillProduitComponent from "./DetailProduitComponent";
import ToggleMagasin from "./ToggleMagasin";
import { customCellFeed } from "../../../components/commons/grid-cell/cellRegistry";

interface DetailProps {
  prod: any;
  statut: any;
  ban: any;
  mag: any;
  setShowAddMagasin: any;
  setListMags: any;
}

interface ProductNameHeaderProps extends GridHeaderCellProps {
  children: any;
  customHeader: string;
}

function DetailComponent(props: DetailProps) {
  const { prod, statut, ban, mag, setShowAddMagasin, setListMags } = props;
  const pollingInterval = useGetPollingInterval();
  const [manquant, setManquant] = useState(false);
  const { data: planogrammes = [] } = useGetPlanoQuery(
    { prod: prod, statut: statut, ban, mag, manquant },
    { pollingInterval: pollingInterval }
  );
  const { data: ensemble = [] } = useGetEnsembleQuery(prod);
  const { data: magasins = [] } = useGetMagasinsQuery({
    banniere: "",
    treeView: false,
  });
  const [data, setData] = useState<any[]>([] as Planogramme[]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (planogrammes.length > 0) {
      const transformedGroupProduit = planogrammes.map((e) => {
        return {
          ...e,
          plano_dt_deb: formatDate(e.plano_dt_deb),
          plano_dt_fin: formatDate(e.plano_dt_fin),
          plano_trsf_dt_entree: formatDate(e.plano_trsf_dt_entree),
          plano_trsf_dt_sortie: formatDate(e.plano_trsf_dt_sortie),
        };
      });
      setData(transformedGroupProduit);
    }
  }, [planogrammes]);

  const HeaderWithTri = (
    props: ProductNameHeaderProps & { tooltip: string }
  ) => {
    let tooltipParagraphs: JSX.Element[] = [];

    if (props.tooltip !== undefined) {
      tooltipParagraphs = (props.tooltip || "")
        .split(/(?=\b\w+:)/)
        .map((segment, index) => <p key={index}>{segment.trim()}</p>);
    }
    const isEmptyTooltip =
      tooltipParagraphs.length === 1 &&
      tooltipParagraphs[0].props.children === "";

    return (
      <>
        {!isEmptyTooltip ? (
          <Tooltip title={parse(props.tooltip)}>
            <span>{props.title}</span>
          </Tooltip>
        ) : props.customHeader ? (
          <ToggleMagasin
            title={props.title!}
            manquant={manquant}
            setManquant={setManquant}
          />
        ) : (
          <>
            <span>{props.title}</span> {props.children}
          </>
        )}
      </>
    );
  };

  const onContextMenu = (e: GridContextMenuEvent) => {
    setShowAddMagasin(e.dataItem.statut_aff === "Manquant");
    setListMags(
      e.dataItem.statut_aff === "Manquant"
        ? [magasins.find((m) => m.mag_num === e.dataItem.magasin.mag_num)]
        : []
    );
  };

  const expandChange = useCallback(
    (event: GridExpandChangeEvent) => {
      if (event.dataIndex !== -1) {
        let i = planogrammes.findIndex(
          (e) => e.plano_seq === event.dataItem.plano_seq
        );
        dispatch(
          planoAPI.util.updateQueryData(
            "getPlano",
            {
              prod: prod,
              statut: statut,
              ban,
              mag,
              manquant,
            },
            (planogramme) => {
              planogramme[i].expanded =
                planogramme[i].expanded === undefined
                  ? true
                  : !planogramme[i].expanded;
              return planogramme;
            }
          )
        );
      }
    },
    [ban, dispatch, mag, manquant, planogrammes, prod, statut]
  );

  return (
    <>
      {magasins.length > 0 && (
        <GridWrapper>
          <Grid
            data={data}
            detail={ensemble.length > 0 ? DetaillProduitComponent : null}
            expandField="expanded"
            onExpandChange={expandChange}
            reorderable={true}
            onContextMenu={onContextMenu}
            resizable
          >
            {planoHeaders.map(
              (column: Header, index: number) =>
                column.isDisplayed && (
                  <GridColumn
                    headerCell={(props) => (
                      <HeaderWithTri
                        {...props}
                        tooltip={column.tooltip || ""}
                        customHeader={column.customHeader || ""}
                      />
                    )}
                    field={column.field}
                    key={column.fieldLabel ?? "" + index}
                    title={column.fieldLabel}
                    format={column.fieldFormat}
                    headerClassName={`${
                      column.fieldFormat === "{0:c}" ? "alignRight" : ""
                    }`}
                    className={`${
                      column.fieldFormat === "{0:c}" ? "alignRight" : ""
                    }`}
                    width={column.width}
                    cells={{
                      data: column.customCell ? customCellFeed(column.customCell) : undefined
                    }}
                  />
                )
            )}
          </Grid>
        </GridWrapper>
      )}
    </>
  );
}

export default React.memo(DetailComponent);
